'use client';

import cx from 'classnames';
import {type ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {Button} from '../atoms/Button';
import {Typography, type TypographyVariant} from '../atoms/Typography';
import {useBrowserWidth} from '../hooks/useBrowserWidth';
import {pebble} from '../sass/base/_exports.sass';
import {ChevronIcon} from '../svg/ChevronIcon';

export const resizeAccordion = (id: string, toggle = false) => {
  const accordionBodyWrapper = document.getElementById(id);
  const accordionBody = document.getElementById(`${id}_inner`);
  if (!accordionBodyWrapper || !accordionBody) return;
  if (toggle) {
    accordionBodyWrapper.style.height = '0px';
  } else {
    accordionBodyWrapper.style.height = `${accordionBody.clientHeight}px`;
  }
};

interface AccordionProps {
  isOpen?: boolean;
  accordionBodyWrapperId?: string;
  accordionBodyClassName?: string;
  onOpen?: () => void;
  onClose?: () => void;
  children: ReactNode;
  className?: string;
  accordionQaId?: string;
  alwaysOpen?: boolean;
  accordionButtonClassName?: string;
  label?: string | null;
  labelVariant?: TypographyVariant;
  labelColor?: string;
  customLabelElement?: ReactNode;
  labelClassName?: string;
  subLabel?: string | null;
  subLabelVariant?: TypographyVariant;
  subLabelColor?: string;
  labelDescription?: ReactNode | string;
  labelDescriptionVariant?: TypographyVariant;
  labelDescriptionColor?: string;
  labelDecoration?: ReactNode;
  additionalLabelContent?: ReactNode;
  chevron?: ReactNode;
  chevronColor?: string;
  uniqueAccordionBodyWrapperId?: string;
  arrowPlacement?: 'left' | 'right';
  ref?: React.Ref<HTMLDivElement>;
}

export const Accordion = ({
  isOpen = true,
  accordionBodyWrapperId = '',
  accordionBodyClassName,
  onOpen,
  onClose,
  children,
  className = '',
  accordionQaId = '',
  alwaysOpen = false,
  accordionButtonClassName = '',
  label = null,
  labelVariant = 'label3',
  labelColor = 'charcoal',
  customLabelElement = null,
  labelClassName = '',
  subLabel = null,
  subLabelVariant = 'header5',
  subLabelColor = labelColor,
  labelDescription,
  labelDescriptionVariant = 'paragraph3',
  labelDescriptionColor = 'pebble',
  labelDecoration = null,
  additionalLabelContent = null,
  chevron = null,
  chevronColor = pebble,
  uniqueAccordionBodyWrapperId,
  arrowPlacement = 'right',
  ref,
}: AccordionProps) => {
  const [open, setOpen] = useState(isOpen);
  const browserWidth = useBrowserWidth({timeout: 150});

  const bodyWrapperId = useMemo(
    () =>
      uniqueAccordionBodyWrapperId ??
      `${accordionBodyWrapperId}-${Math.floor(Math.random() * 999999) + 1}`,
    [uniqueAccordionBodyWrapperId, accordionBodyWrapperId]
  );

  const handleToggleAccordion = useCallback(() => {
    resizeAccordion(bodyWrapperId, true);
    setOpen(!open);
    if (open && onClose) onClose();
    else if (onOpen) onOpen();
  }, [bodyWrapperId, open, onOpen, onClose]);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: want to run when children or browser width change
  useEffect(() => {
    resizeAccordion(bodyWrapperId, !open);

    if (ref && typeof ref !== 'function' && ref.current) {
      const observer = new ResizeObserver(() => {
        resizeAccordion(bodyWrapperId, !open);
      });
      observer.observe(ref.current);
      return () => observer.disconnect();
    }
  }, [children, open, bodyWrapperId, browserWidth]);

  // can't use string concat - these are elements
  const labelToShow = useMemo(
    () =>
      labelDecoration ? (
        <>
          {label} {labelDecoration}
        </>
      ) : (
        label
      ),
    [label, labelDecoration]
  );

  const LabelElement = useMemo(() => {
    if (customLabelElement) return customLabelElement;

    if (subLabel || labelDescription || additionalLabelContent) {
      return (
        <div className="accordion__label-wrapper">
          <Typography variant={labelVariant} className={labelClassName} color={labelColor}>
            {labelToShow}
          </Typography>
          {subLabel && (
            <Typography variant={subLabelVariant} color={subLabelColor}>
              {subLabel}
            </Typography>
          )}
          {/* Label description is an element */}
          {labelDescription && typeof labelDescription !== 'string' && labelDescription}
          {/* Label description is a simple string */}
          {labelDescription && typeof labelDescription === 'string' && (
            <Typography variant={labelDescriptionVariant} color={labelDescriptionColor}>
              {labelDescription}
            </Typography>
          )}
          {additionalLabelContent}
        </div>
      );
    }
    if (labelToShow) {
      return (
        <Typography variant={labelVariant} className={labelClassName} color={labelColor}>
          {labelToShow}
        </Typography>
      );
    }
    return null;
  }, [
    subLabel,
    labelDescription,
    additionalLabelContent,
    labelVariant,
    labelClassName,
    labelToShow,
    subLabelVariant,
    labelDescriptionVariant,
    labelColor,
    subLabelColor,
    labelDescriptionColor,
    customLabelElement,
  ]);

  return (
    <div className={cx('accordion__container', className)} data-qa-id={accordionQaId} ref={ref}>
      {!alwaysOpen && (
        <Button
          className={cx(
            'accordion__button',
            {[`accordion__arrow-${arrowPlacement}`]: arrowPlacement},
            accordionButtonClassName
          )}
          size="small"
          variant="transparent"
          data-qa-id={`accordion-show-hide-content_${label?.replaceAll?.(' ', '_') ?? uniqueAccordionBodyWrapperId?.replaceAll(' ', '_')}`}
          onClick={handleToggleAccordion}
          // Without these three props, TypeScript complains about missing props
          // When Button.jsx is converted to TypeScript, these props can be removed or improved
          data-qa-value=""
          ariaLabel=""
          id=""
        >
          {LabelElement}
          {chevron ?? (
            <ChevronIcon direction={open ? 'up' : 'down'} ratio={0.5} color={chevronColor} />
          )}
        </Button>
      )}
      <div className="accordion__body-wrapper" id={bodyWrapperId}>
        <div id={`${bodyWrapperId}_inner`} className={accordionBodyClassName}>
          {children}
        </div>
      </div>
    </div>
  );
};
